import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, FileText, Download, Trash2, Loader, ChevronLeft, ChevronRight } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import api from '../../services/api';
import alertService from '../../utils/alert';
import { formatDateTime } from '../../utils/formatDate';
import { PAGE } from '../../components/crm/ui';

const PAGE_SIZE = 20;

/**
 * Phase 26 — the forms module had a per-CRM-record "Form Activity" tab
 * (Phase 18) and per-submission PDF export (Phase 22), but no page to
 * simply browse every response a form has ever received — an admin
 * checking "did anyone actually fill this out" had nowhere to look besides
 * the read-only Analytics counts. This is that missing list view.
 */
export default function FormSubmissionsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [formName, setFormName] = useState('');
  const [submissions, setSubmissions] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const fetchSubmissions = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/forms/${id}/submissions`, { params: { page, limit: PAGE_SIZE } });
      setSubmissions(data.data || []);
      setTotal(data.total || 0);
    } catch (err) {
      alertService.toast('error', err.response?.data?.message || 'Failed to load submissions');
    } finally {
      setLoading(false);
    }
  }, [id, page]);

  useEffect(() => {
    api.get(`/forms/${id}`).then(({ data }) => setFormName(data.data.form.name)).catch(() => {});
  }, [id]);

  useEffect(() => { fetchSubmissions(); }, [fetchSubmissions]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const downloadPdf = async (submissionId) => {
    const res = await api.get(`/forms/${id}/submissions/${submissionId}/pdf`, { responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = `submission_${submissionId}.pdf`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await api.get(`/forms/${id}/submissions/export`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `${formName || 'submissions'}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alertService.toast('error', err.response?.data?.message || 'Failed to export CSV');
    } finally {
      setExporting(false);
    }
  };

  const deleteSubmission = async (submissionId) => {
    if (!(await alertService.confirm('Delete?', 'Delete this submission permanently? This does not affect any Lead/Contact/Deal/Task it already created.', 'danger', 'Delete'))) return;
    setDeletingId(submissionId);
    try {
      await api.delete(`/forms/${id}/submissions/${submissionId}`);
      alertService.toast('success', 'Submission deleted');
      fetchSubmissions();
    } catch (err) {
      alertService.toast('error', err.response?.data?.message || 'Failed to delete submission');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className={PAGE}>
      <PageHeader
        title={`${formName || 'Form'} — Submissions`}
        subtitle={`${total} response${total === 1 ? '' : 's'} total`}
        icon={FileText}
        actions={<>
          <button onClick={() => navigate(`/dashboard/forms/${id}`)} className="flex items-center gap-1.5 px-3 h-9 text-[13px] font-semibold text-[#0176D3] bg-white border border-[#C9C9C9] hover:bg-[#F3F3F3] rounded whitespace-nowrap">
            <ArrowLeft size={14} /> Back
          </button>
          <button
            onClick={exportCsv}
            disabled={exporting || !total}
            className="flex items-center gap-1.5 px-3 h-9 text-[13px] font-semibold text-white bg-[#0176D3] hover:bg-[#014486] rounded disabled:opacity-50 transition-colors whitespace-nowrap"
          >
            {exporting ? <Loader size={14} className="animate-spin" /> : <Download size={14} />}
            Export CSV
          </button>
        </>}
      />

      {loading ? (
        <div className="flex items-center justify-center py-16"><Loader size={20} className="animate-spin text-[#706E6B]" /></div>
      ) : !submissions.length ? (
        <div className="text-center py-16">
          <FileText size={32} className="mx-auto mb-2 text-[#D8DDE6]" />
          <p className="text-[13px] text-[#706E6B]">No submissions yet.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {submissions.map((s) => {
            const expanded = expandedId === s._id;
            const values = s.values || {};
            return (
              <div key={s._id} className="border border-[#D8DDE6] rounded-[4px] bg-white overflow-hidden">
                <button
                  type="button"
                  onClick={() => setExpandedId(expanded ? null : s._id)}
                  className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-[#F3F3F3]"
                >
                  <span className="text-[13px] font-bold text-[#181818]">{formatDateTime(s.createdAt)}</span>
                  <span className="text-[11px] text-[#706E6B]">{Object.keys(values).length} field{Object.keys(values).length === 1 ? '' : 's'}</span>
                </button>
                {expanded && (
                  <div className="px-4 py-3 border-t border-[#F0F1F3] bg-[#FAFAFA] space-y-3">
                    <div className="space-y-1.5">
                      {Object.entries(values).length === 0 && <p className="text-[12px] text-[#706E6B]">No field values recorded.</p>}
                      {Object.entries(values).map(([key, val]) => (
                        <div key={key} className="flex items-start gap-2 text-[12px]">
                          <span className="text-[#706E6B] font-mono shrink-0">{key}:</span>
                          <span className="text-[#181818] break-words">{typeof val === 'object' ? JSON.stringify(val) : String(val)}</span>
                        </div>
                      ))}
                    </div>
                    <div className="flex items-center gap-4 pt-1">
                      <button
                        onClick={() => downloadPdf(s._id)}
                        className="inline-flex items-center gap-1 text-[11px] font-bold text-[#0176D3] hover:underline"
                      >
                        <Download size={11} /> Download PDF
                      </button>
                      <button
                        onClick={() => deleteSubmission(s._id)}
                        disabled={deletingId === s._id}
                        className="inline-flex items-center gap-1 text-[11px] font-bold text-[#706E6B] hover:text-[#EA001E] disabled:opacity-50"
                      >
                        {deletingId === s._id ? <Loader size={11} className="animate-spin" /> : <Trash2 size={11} />}
                        Delete
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
            className="flex items-center gap-1 px-3 py-1.5 text-[12px] font-bold text-[#706E6B] hover:text-[#181818] disabled:opacity-40"
          >
            <ChevronLeft size={14} /> Prev
          </button>
          <span className="text-[12px] text-[#706E6B]">Page {page} of {totalPages}</span>
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages}
            className="flex items-center gap-1 px-3 py-1.5 text-[12px] font-bold text-[#706E6B] hover:text-[#181818] disabled:opacity-40"
          >
            Next <ChevronRight size={14} />
          </button>
        </div>
      )}
    </div>
  );
}
